import { useMemoizedFn } from "ahooks";
import dayjs from "dayjs";
import useSWR from "swr";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useLocalStorage } from "react-use";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarPurple500Icon from "@mui/icons-material/StarPurple500";
import {
  Accordion,
  AccordionSummary,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";

import { ApiGetFlightRoutesSampleParams, ApiRoutesItem, getFlightRoutesV2 } from "@/api/routes";
import { getNotUpdatedPanorama } from "@/api/routesFlights";
import AlertModal, { AlertModalRefProps } from "@/components/AlertModal";
import DateFastForward from "@/components/DateFastForward";
import IntroRoutes from "@/components/IntroRoutes";
import RouteVoteDialog from "@/components/RouteVoteDialog";
import { ScrollTopBar } from "@/components/ScrollTopBar";
import TrackPriceCard from "@/components/TrackPriceCard";
import { AlertTypeEnum } from "@/constants";
import { ListViewType } from "@/constants";
import { ROUTE_NUMBER_COUNT } from "@/constants/config";
import { ROUTES_PAGE_CHANGE_CABIN, ROUTES_PAGE_INIT_LOADDATA } from "@/constants/eventName";
import { FREE_PANORAMA_RANGE } from "@/constants/membership";
import { useAlertParamsContext } from "@/context/AlertParamsContext";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useCabinsColorIdentify from "@/hooks/useCabinsColorIdentify";
import useEvent from "@/hooks/useEvent";
import { dispatchEvent, useListenEvent } from "@/hooks/useEventEmitter";
import useMore from "@/hooks/useMore";
import usePlatform from "@/hooks/usePlatform";
import { useQuery } from "@/hooks/useQuery";
import { useRefreshPanorama } from "@/hooks/useRefreshPanorama";
import useThemeData from "@/hooks/useThemeData";
import { ConditionType, createSearchQuery } from "@/pages/Routes/hooks/useCondition";
import { CabinsType } from "@/typings";

import CabinsColorIdentify from "./components/CabinsColorIdentify";
import FilterBar from "./components/FilterBar";
import FlightDetailsModal from "./components/FlightDetails";
import MobileSearchBar from "./components/MobileSearchBar";
import RoutesList from "./components/RoutesList";
import RoutesListSkeleton from "./components/RoutesListSkeleton";
import RoutesListTable from "./components/RoutesListTable";
import SearchBar from "./components/SearchBar";
import Sort from "./components/Sort";
import { initialRouteSort, sortFn } from "./components/Sort/sortConfig";
import ViewType from "./components/ViewType";
import useCheckCondition from "./hooks/useCheckCondition";
import { LocationEnum } from "./hooks/useLocationFilter";

const RoutesPage = () => {
  const theme = useThemeData();
  const { isMobile, isPc } = usePlatform();
  const { checkCondition } = useCheckCondition();
  const store = useGlobalStoreContext();
  const show_upsell: boolean = store?.show_upsell;

  const closestAirportRef = useRef(store?.closest_airport);

  closestAirportRef.current = store?.closest_airport;

  const [condition, setCondition] = useState<ConditionType>();
  const [searchData, setSearchData] = useState<ApiRoutesItem[]>([]);
  const [filterData, setFilterData] = useState<ApiRoutesItem[]>([]);
  const [sortKey, setSortKey] = useState<number>(0);
  const [isLoading, setLoading] = useState(true);
  const [totalTitle, setTotalTitle] = useState("");

  const [listType, setListType] = useLocalStorage("panorama_list_type", ListViewType.Table);

  const { isLoading: notUpdatedPanoramaLoading, data: notUpdatedPanorama } = useSWR(
    "routes_nu_programs",
    () => getNotUpdatedPanorama()
  );

  const { setRoutesAlertParams } = useAlertParamsContext();

  const [openRouteRequest, setOpenRouteRequest] = useState(false);
  const [open, setOpen] = useState(false);
  const [currentParams, setCurrentParams] = useState<
    ApiGetFlightRoutesSampleParams & { type: CabinsType }
  >();

  const { cabinsColorIdentifyVisible, hideColorIdentify } = useCabinsColorIdentify();

  const modalRef = useRef<AlertModalRefProps>(null);

  const query = useQuery<{
    startDate: string;
    endDate: string;
    programs: string[];
    airlines: string[];
    cabins: string[];
    nonStop: string[];
    pax: number;
    children: number;
  }>();

  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);

  const [trackPriceOpen, setTrackPriceOpen] = useState(false);
  useListenEvent(ROUTES_PAGE_INIT_LOADDATA, (data: ConditionType) => {
    if (data.fromLocation.length !== 0 && data.toLocation.length !== 0) {
      setCondition(data);
      handleSearch(data);
    }
  });

  const { historyData, handleNext, hasMore } = useMore(filterData, { pageSize: 30 });
  const filterFromQuery = useMemo(() => {
    const passenger = Number(query.pax || 0) + Number(query.children || 0);
    if (passenger) {
      return {
        seats: [passenger, passenger, passenger, passenger],
      };
    } else {
      return undefined;
    }
  }, [query.pax, query.children]);

  const { refreshIds } = useRefreshPanorama({
    isReady: !isLoading && !notUpdatedPanoramaLoading,
    data: historyData,
    setData: setSearchData,
    whiteList: notUpdatedPanorama || [],
  });

  useEffect(() => {
    // 全局的alertParams用完即清除
    return () => {
      setRoutesAlertParams({});
    };
  }, [setRoutesAlertParams]);

  const handleSearch = useEvent(async (data: ConditionType, isClick?: boolean) => {
    if (data) {
      const errMsg = checkCondition(data);

      if (errMsg) {
        setLoading(false);
        return;
      }

      const nextParams = createSearchQuery(data);

      const nextSearch = createSearchParams(nextParams).toString();

      // setSearchParams(nextSearch);

      if (isClick) {
        if (data.searchMode === "true") {
          navigate(`/routes-flights?${nextSearch}`, { replace: true });
        } else {
          navigate(`/routes?${nextSearch}`, { replace: true });
        }
      }
      setCondition(data);

      const fromLocation = {
        regions: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.REGION)
          .map((ele) => ele.code),

        countries: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.COUNTRY)
          .map((ele) => ele.code),

        airports: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.AIRPORT)
          .map((ele) => ele.code),
      };
      const toLocation = {
        regions: data.toLocation
          .filter((ele) => ele.type === LocationEnum.REGION)
          .map((ele) => ele.code),

        countries: data.toLocation
          .filter((ele) => ele.type === LocationEnum.COUNTRY)
          .map((ele) => ele.code),

        airports: data.toLocation
          .filter((ele) => ele.type === LocationEnum.AIRPORT)
          .map((ele) => ele.code),
      };

      const params = {
        date_range: {
          from: dayjs(data.startDate).format("YYYY-MM-DD"),
          to: dayjs(data.endDate).format("YYYY-MM-DD"),
        },
        origins: fromLocation,
        arrivals: toLocation,
        cabins: data.cabins.split("&").map((item) => item.trim()),
      };

      try {
        // 手动出发搜索按钮时缓存搜索条件到本地
        if (isClick) {
          localStorage.setItem("routes_search_conditions", JSON.stringify(data));
        }

        setLoading(true);
        setSearchData([]);
        await getFlightRoutesV2(params, appendSearchData, setDialogOpen, setLoading, show_upsell);
      } catch (error) {
        console.log(error);
      }
    }
  });

  const appendSearchData = (data: ApiRoutesItem[], stopLoading = false) => {
    if (stopLoading) {
      const initialData = initialRouteSort(data, closestAirportRef.current);
      setSearchData(initialData);
      setTimeout(() => {
        setLoading(false);
      }, 200);
    } else {
      setSearchData(data);
    }
  };

  const filterChange = useCallback(
    (v: ApiRoutesItem[]) => {
      const sortFilterData = sortFn(v, sortKey);
      setFilterData(sortFilterData);
    },
    [sortKey]
  );

  const sortChange = useCallback(
    (sortKey: number) => {
      const sortFilterData = sortFn(filterData, sortKey);
      setFilterData(sortFilterData);
      setSortKey(sortKey);
    },
    [filterData]
  );

  const handleOpen = useMemoizedFn((v) => {
    setCurrentParams({
      program: v.program,
      origin: v.route.split("-")[0],
      arrival: v.route.split("-")[1],
      date: dayjs(v.date).format("YYYY-MM-DD"),
      type: v.type,
    });

    setOpen(true);
  });

  useEffect(() => {
    let text = "";
    if (searchData.length > 0) {
      if (filterData.length === searchData.length) {
        text = `${Number(searchData.length).toLocaleString()}` + " results";
      } else {
        text =
          `${Number(filterData.length).toLocaleString()} / ${Number(
            searchData.length
          ).toLocaleString()}` + " results";
      }
    }
    // FIXME: 延迟更新，防止闪烁，暂时没有想到更优的方式
    const timer = setTimeout(() => {
      setTotalTitle(text);
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [searchData, filterData]);
  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>AwardTool | Panorama Search</title>
        <meta
          name="description"
          content={
            "Search across unlimited regions, cities, and airports with 90-day range for " +
            ROUTE_NUMBER_COUNT.toLocaleString() +
            "+ popular routes."
          }
        />
      </Helmet>
      <div className="max-w-7xl mx-auto">
        {isMobile ? (
          <>
            <MobileSearchBar data={condition} onSearch={handleSearch} isLoading={isLoading} />
            <div className="px-[10px] mt-[16px]">
              <FilterBar
                dataSourceList={searchData}
                onChange={filterChange}
                initValues={filterFromQuery}
              />
            </div>
          </>
        ) : (
          <div className="px-[10px]">
            <div className="py-[16px]">
              <SearchBar onSearch={handleSearch} isLoading={isLoading} />
            </div>

            <FilterBar
              initValues={filterFromQuery}
              dataSourceList={searchData}
              onChange={filterChange}
            />
          </div>
        )}

        <div className="mt-[13px] mb-[1px]">
          <IntroRoutes useWhere="routes" />
        </div>

        <div className="mt-[10px] mx-[10px]">
          <TrackPriceCard
            dateRange={[+query.startDate * 1000, +query.endDate * 1000]}
            value={trackPriceOpen}
            onChange={(value) => {
              setTrackPriceOpen(value);
              modalRef.current && modalRef.current.show(AlertTypeEnum.Panorama, {});
            }}
            onRouteRequest={() => setOpenRouteRequest(true)}
          />
        </div>

        {isLoading ? (
          <div className="mt-[13px] mx-[10px]">
            <LinearProgress />
          </div>
        ) : null}

        {filterData.length && cabinsColorIdentifyVisible ? (
          <div className="flex items-center justify-between mx-[10px] mt-[5px] mb-[6px] overflow-x-auto no-scrollbar">
            <CabinsColorIdentify
              onClose={hideColorIdentify}
              onClick={(v) => {
                if (v) {
                  dispatchEvent(ROUTES_PAGE_CHANGE_CABIN, v);
                }
              }}
            />
          </div>
        ) : null}

        {filterData.length === 0 && !isLoading ? (
          <div className="mt-[80px] text-center">
            {show_upsell && (
              <div>
                <Typography color="textSecondary">
                  {"Free user can only access " + FREE_PANORAMA_RANGE + " days of data."}
                </Typography>
                <Typography color="primary">Upgrade to PRO to enjoy 365 days of data.</Typography>
              </div>
            )}
            <Typography color="textSecondary">No options matching your filters</Typography>

            <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
          </div>
        ) : (
          <div className="px-[10px] mt-[-7px]">
            {filterData.length ? (
              <>
                <div className="flex items-center py-[12px]">
                  <Typography variant="body1" color="textPrimary" className="!mr-[10px]">
                    {totalTitle}
                  </Typography>
                  {isPc && <ViewType value={listType} onChange={setListType} />}
                  <div className="flex-1" />
                  <div className="flex">
                    {/* {isPc && !isLoading && <DateFastForward />} */}
                    {isPc && <DateFastForward />}
                    <Sort value={sortKey} onChange={sortChange} />
                  </div>
                </div>
                {/* {isMobile && !isLoading && ( */}
                {isMobile && (
                  <div className="flex justify-end pb-[10px] mt-[-2px]">
                    <DateFastForward />
                  </div>
                )}

                {listType === ListViewType.List ? (
                  <RoutesList data={historyData} onClick={handleOpen} refreshIds={refreshIds} />
                ) : (
                  <RoutesListTable
                    data={historyData}
                    onClick={handleOpen}
                    refreshIds={refreshIds}
                  />
                )}
              </>
            ) : null}
            {hasMore ? (
              <Accordion
                expanded={false}
                sx={{
                  boxShadow: "none",
                  marginTop: "-1px",
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <AccordionSummary onClick={(e) => e.stopPropagation()}>
                  <div className="w-full flex items-center justify-center" onClick={handleNext}>
                    <IconButton color="primary">
                      <ExpandMoreIcon />
                    </IconButton>
                    <Typography color="textPrimary" className="mr-[10px]">
                      More Routes
                    </Typography>
                    <StarPurple500Icon fontSize="small" className="ml-[4px] mb-[1px]" />
                  </div>
                </AccordionSummary>
              </Accordion>
            ) : null}
            {isLoading ? <RoutesListSkeleton count={10} showPro={show_upsell} /> : null}
          </div>
        )}
      </div>

      {open && (
        <FlightDetailsModal
          open={open}
          onClose={() => setOpen(false)}
          params={currentParams}
          whiteList={notUpdatedPanorama}
        />
      )}

      <RouteVoteDialog open={openRouteRequest} onOpen={() => setOpenRouteRequest(false)} />

      <AlertModal ref={modalRef} onReset={() => setTrackPriceOpen(false)} />

      <Dialog
        onClose={() => {
          setDialogOpen(false);
        }}
        open={dialogOpen}
        sx={{
          maxWidth: "750px",
          margin: "auto",
          "& .MuiDialog-paper": {
            margin: 0,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle>
          <Typography color="textPrimary">Search Condition Error</Typography>
        </DialogTitle>
        <DialogContent dividers className="pt-[20px] !px-[20px]">
          Search condition is wrong, please check your search condition.
        </DialogContent>
      </Dialog>
      <ScrollTopBar />
    </>
  );
};

export default RoutesPage;
